import { useState } from "react";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import ConfirmationModal from "../../components/ui/ConfirmationModal";
import Input from "../../components/ui/Input";
import Select, { type SelectOption } from "../../components/ui/Select";
import { useToast } from "../../contexts/ToastContext";
import {
	useDeleteUserMutation,
	useListUsersQuery,
	useUpdateUserMutation,
} from "../../store/api/usersApi";
import type { User } from "../../types";
import CreateUserForm from "./components/CreateUserForm";
import EditUserModal from "./components/EditUserModal";
import ViewUserDetailsModal from "./components/ViewUserDetailsModal";

const ROLE_OPTIONS: SelectOption[] = [
	{ value: "", label: "All Roles" },
	{ value: "admin", label: "Admin" },
	{ value: "tenant", label: "Tenant" },
	{ value: "technician", label: "Technician" },
	{ value: "agent", label: "Agent" },
];

const ROLE_REASSIGN_OPTIONS: SelectOption[] = [
	{ value: "admin", label: "Admin" },
	{ value: "tenant", label: "Tenant" },
	{ value: "technician", label: "Technician" },
	{ value: "agent", label: "Agent" },
];

const UserManagementPage = () => {
	const { success, error: showError } = useToast();
	const [page, setPage] = useState(1);
	const [roleFilter, setRoleFilter] = useState<string>("");
	const [searchQuery, setSearchQuery] = useState("");
	const [showCreateForm, setShowCreateForm] = useState(false);
	const [selectedUser, setSelectedUser] = useState<User | null>(null);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [isViewModalOpen, setIsViewModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [userToDelete, setUserToDelete] = useState<{
		id: string;
		name: string;
	} | null>(null);
	const [isRoleChangeModalOpen, setIsRoleChangeModalOpen] = useState(false);
	const [roleChangeData, setRoleChangeData] = useState<{
		userId: string;
		userName: string;
		newRole: string;
		currentRole: string;
	} | null>(null);
	const [isStatusChangeModalOpen, setIsStatusChangeModalOpen] = useState(false);
	const [statusChangeData, setStatusChangeData] = useState<{
		userId: string;
		userName: string;
		newStatus: boolean;
		currentStatus: boolean;
	} | null>(null);

	const {
		data: usersData,
		isLoading,
		error,
		refetch,
	} = useListUsersQuery({
		page,
		page_size: 20,
		role: roleFilter || undefined,
		search: searchQuery || undefined,
		ordering: "-created_at",
	});

	const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
	const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
	const [editingRole, setEditingRole] = useState<{
		userId: string;
		currentRole: string;
	} | null>(null);

	const handleDeleteClick = (userId: string, userName: string) => {
		setUserToDelete({ id: userId, name: userName });
		setIsDeleteModalOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (!userToDelete) return;

		try {
			await deleteUser(userToDelete.id).unwrap();
			success(`User "${userToDelete.name}" deleted successfully`);
			setIsDeleteModalOpen(false);
			setUserToDelete(null);
			refetch();
		} catch (error: any) {
			console.error("Failed to delete user:", error);
			showError(
				error?.data?.detail ||
					error?.data?.message ||
					"Failed to delete user. Please try again.",
			);
		}
	};

	const handleRoleChangeClick = (
		userId: string,
		newRole: string,
		userName: string,
	) => {
		const currentUser = usersData?.results.find((u) => u.id === userId);
		if (currentUser?.role === newRole) {
			setEditingRole(null);
			return;
		}

		setRoleChangeData({
			userId,
			userName,
			newRole,
			currentRole: currentUser?.role || "",
		});
		setIsRoleChangeModalOpen(true);
	};

	const handleConfirmRoleChange = async () => {
		if (!roleChangeData) return;

		try {
			await updateUser({
				id: roleChangeData.userId,
				data: { role: roleChangeData.newRole as any },
			}).unwrap();
			success(
				`User role changed from "${roleChangeData.currentRole}" to "${roleChangeData.newRole}"`,
			);
			setEditingRole(null);
			setIsRoleChangeModalOpen(false);
			setRoleChangeData(null);
			refetch();
		} catch (error: any) {
			console.error("Failed to update user role:", error);
			showError(
				error?.data?.detail ||
					error?.data?.message ||
					"Failed to update user role. Please try again.",
			);
			setEditingRole(null);
		}
	};

	const handleStatusToggleClick = (
		userId: string,
		currentStatus: boolean,
		userName: string,
	) => {
		const newStatus = !currentStatus;
		setStatusChangeData({
			userId,
			userName,
			newStatus,
			currentStatus,
		});
		setIsStatusChangeModalOpen(true);
	};

	const handleConfirmStatusChange = async () => {
		if (!statusChangeData) return;

		try {
			await updateUser({
				id: statusChangeData.userId,
				data: { is_active: statusChangeData.newStatus } as any,
			}).unwrap();
			success(
				`User "${statusChangeData.userName}" has been ${
					statusChangeData.newStatus ? "activated" : "deactivated"
				}`,
			);
			setIsStatusChangeModalOpen(false);
			setStatusChangeData(null);
			refetch();
		} catch (error: any) {
			console.error("Failed to update user status:", error);
			showError(
				error?.data?.detail ||
					error?.data?.message ||
					"Failed to update user status. Please try again.",
			);
		}
	};

	const handleViewUser = (user: User) => {
		setSelectedUser(user);
		setIsViewModalOpen(true);
	};

	const handleEditUser = (user: User) => {
		setSelectedUser(user);
		setIsEditModalOpen(true);
	};

	const handleEditSuccess = () => {
		setIsEditModalOpen(false);
		setSelectedUser(null);
		refetch();
	};

	if (error) {
		return (
			<div className="flex flex-col items-center justify-center min-h-screen space-y-4">
				<Icon name="AlertCircle" size={48} className="text-destructive" />
				<h2 className="text-xl font-semibold text-foreground">
					Error Loading Users
				</h2>
				<p className="text-muted-foreground">
					Failed to load users. Please try again.
				</p>
				<Button onClick={() => refetch()}>Retry</Button>
			</div>
		);
	}

	return (
		<div className="w-full">
			<div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
				{/* Header */}
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
					<div>
						<h1 className="text-2xl sm:text-3xl font-bold text-foreground">
							User Management
						</h1>
						<p className="text-muted-foreground mt-1">
							Manage users, roles, and permissions
						</p>
					</div>
					<div className="flex-shrink-0">
						<Button
							onClick={() => setShowCreateForm(!showCreateForm)}
							iconName={showCreateForm ? "X" : "UserPlus"}
							iconPosition="left"
							className="w-full sm:w-auto"
						>
							{showCreateForm ? "Cancel" : "Create User"}
						</Button>
					</div>
				</div>

				{/* Create User Form */}
				{showCreateForm && (
					<div className="bg-card rounded-lg border border-border p-6">
						<CreateUserForm
							onSuccess={() => {
								setShowCreateForm(false);
								refetch();
							}}
							onCancel={() => setShowCreateForm(false)}
						/>
					</div>
				)}

				{/* Filters */}
				<div className="bg-card rounded-lg border border-border p-4">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div>
							<label className="mb-2 block text-sm font-medium text-foreground">
								Search Users
							</label>
							<div className="relative">
								<Icon
									name="Search"
									size={16}
									className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
								/>
								<Input
									type="text"
									placeholder="Search by name or email..."
									value={searchQuery}
									onChange={(e) => {
										setSearchQuery(e.target.value);
										setPage(1);
									}}
									className="pl-10"
								/>
							</div>
						</div>
						<div>
							<label className="mb-2 block text-sm font-medium text-foreground">
								Filter by Role
							</label>
							<Select
								value={roleFilter}
								options={ROLE_OPTIONS}
								onValueChange={(value) => {
									setRoleFilter(value as string);
									setPage(1);
								}}
							/>
						</div>
					</div>
				</div>

				{/* Users List */}
				<div className="bg-card rounded-lg border border-border">
					{isLoading ? (
						<div className="flex items-center justify-center p-12">
							<Icon
								name="Loader2"
								size={32}
								className="animate-spin text-primary"
							/>
						</div>
					) : usersData && usersData.results.length > 0 ? (
						<>
							<div className="overflow-x-auto -mx-6 sm:mx-0">
								<div className="inline-block min-w-full align-middle sm:px-0">
									<table className="min-w-full divide-y divide-border">
										<thead className="bg-muted border-b border-border">
											<tr>
												<th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
													User
												</th>
												<th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
													Email
												</th>
												<th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
													Role
												</th>
												<th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
													Status
												</th>
												<th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
													Created
												</th>
												<th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
													Actions
												</th>
											</tr>
										</thead>
										<tbody className="divide-y divide-border">
											{usersData.results.map((user) => (
												<tr key={user.id} className="hover:bg-muted/50">
													<td className="px-6 py-4 whitespace-nowrap">
														<div className="flex items-center">
															<div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
																<Icon
																	name="User"
																	size={20}
																	className="text-primary"
																/>
															</div>
															<div className="ml-4">
																<div className="text-sm font-medium text-foreground">
																	{user.first_name} {user.last_name}
																</div>
																<div className="text-sm text-muted-foreground">
																	{user.phone || "No phone"}
																</div>
															</div>
														</div>
													</td>
													<td className="px-6 py-4 whitespace-nowrap">
														<div className="text-sm text-foreground">
															{user.email}
														</div>
													</td>
													<td className="px-6 py-4 whitespace-nowrap relative">
														{editingRole?.userId === user.id ? (
															<div className="flex items-center space-x-2 relative z-10">
																<Select
																	value={editingRole.currentRole}
																	options={ROLE_REASSIGN_OPTIONS}
																	onValueChange={(value) => {
																		if (value !== editingRole.currentRole) {
																			handleRoleChangeClick(
																				user.id,
																				value as string,
																				`${user.first_name} ${user.last_name}`,
																			);
																		}
																	}}
																	className="w-36"
																/>
																<Button
																	variant="ghost"
																	size="sm"
																	onClick={() => setEditingRole(null)}
																	iconName="X"
																	title="Cancel"
																/>
															</div>
														) : (
															<span className="px-2 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary capitalize">
																{user.role === "tenant" && user.property_owner
																	? "Property Owner"
																	: user.role === "tenant"
																	? "Tenant"
																	: user.role}
															</span>
														)}
													</td>
													<td className="px-6 py-4 whitespace-nowrap">
														<button
															onClick={() =>
																handleStatusToggleClick(
																	user.id,
																	user.is_active ?? true,
																	`${user.first_name} ${user.last_name}`,
																)
															}
															disabled={isUpdating}
															className="flex items-center space-x-2 hover:opacity-80 transition-opacity cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
															title={`Click to ${
																user.is_active ? "deactivate" : "activate"
															}`}
														>
															<div
																className={`w-2 h-2 rounded-full ${
																	user.is_active ? "bg-success" : "bg-error"
																}`}
															/>
															<span className="text-sm text-foreground">
																{user.is_active ? "Active" : "Inactive"}
															</span>
														</button>
													</td>
													<td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
														{new Date(user.created_at).toLocaleDateString()}
													</td>
													<td className="px-6 py-3 whitespace-nowrap text-right text-sm font-medium">
														<div className="flex items-center justify-end space-x-2">
															<Button
																variant="ghost"
																size="sm"
																onClick={() => handleViewUser(user)}
																disabled={isUpdating}
																iconName="Eye"
																title="View Details"
															>
																View
															</Button>
															<Button
																variant="ghost"
																size="sm"
																onClick={() => handleEditUser(user)}
																disabled={isUpdating}
																iconName="Edit"
																title="Edit User"
															>
																Edit
															</Button>
															<Button
																variant="ghost"
																size="sm"
																onClick={() =>
																	setEditingRole({
																		userId: user.id,
																		currentRole: user.role,
																	})
																}
																disabled={
																	isUpdating || editingRole?.userId === user.id
																}
																iconName="UserCog"
																title="Change Role"
															>
																Role
															</Button>
															<Button
																variant="ghost"
																size="sm"
																onClick={() =>
																	handleDeleteClick(
																		user.id,
																		`${user.first_name} ${user.last_name}`,
																	)
																}
																disabled={isUpdating}
																iconName="Trash2"
																className="text-destructive hover:text-destructive"
																title="Delete User"
															>
																Delete
															</Button>
														</div>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>

							{/* Pagination */}
							{(usersData.next || usersData.previous) && (
								<div className="px-6 py-4 border-t border-border flex items-center justify-between">
									<Button
										variant="outline"
										onClick={() => setPage((p) => Math.max(1, p - 1))}
										disabled={!usersData.previous}
										iconName="ChevronLeft"
										iconPosition="left"
									>
										Previous
									</Button>
									<span className="text-sm text-muted-foreground">
										Page {page} of {Math.ceil(usersData.count / 20)}
									</span>
									<Button
										variant="outline"
										onClick={() => setPage((p) => p + 1)}
										disabled={!usersData.next}
										iconName="ChevronRight"
										iconPosition="right"
									>
										Next
									</Button>
								</div>
							)}
						</>
					) : (
						<div className="text-center py-12">
							<Icon
								name="Users"
								size={48}
								className="mx-auto text-muted-foreground mb-4"
							/>
							<h3 className="text-lg font-medium text-foreground mb-2">
								No users found
							</h3>
							<p className="text-muted-foreground">
								{searchQuery || roleFilter
									? "Try adjusting your filters"
									: "Get started by creating a new user"}
							</p>
						</div>
					)}
				</div>

				{/* Modals */}
				{selectedUser && (
					<>
						<ViewUserDetailsModal
							isOpen={isViewModalOpen}
							onClose={() => {
								setIsViewModalOpen(false);
								setSelectedUser(null);
							}}
							user={selectedUser}
							onEdit={() => {
								setIsViewModalOpen(false);
								setIsEditModalOpen(true);
							}}
						/>

						<EditUserModal
							isOpen={isEditModalOpen}
							onClose={() => {
								setIsEditModalOpen(false);
								setSelectedUser(null);
							}}
							onSuccess={handleEditSuccess}
							user={selectedUser}
						/>
					</>
				)}

				{/* Delete Confirmation Modal */}
				<ConfirmationModal
					isOpen={isDeleteModalOpen}
					onClose={() => {
						setIsDeleteModalOpen(false);
						setUserToDelete(null);
					}}
					onConfirm={handleConfirmDelete}
					title="Delete User"
					message={`Are you sure you want to delete user "${userToDelete?.name}"? This action cannot be undone.`}
					confirmButtonText="Delete"
					isDestructive={true}
					isLoading={isDeleting}
				/>

				{/* Role Change Confirmation Modal */}
				<ConfirmationModal
					isOpen={isRoleChangeModalOpen}
					onClose={() => {
						setIsRoleChangeModalOpen(false);
						setRoleChangeData(null);
						setEditingRole(null);
					}}
					onConfirm={handleConfirmRoleChange}
					title="Change User Role"
					message={`Are you sure you want to change "${roleChangeData?.userName}" role from "${roleChangeData?.currentRole}" to "${roleChangeData?.newRole}"?`}
					confirmButtonText="Change Role"
					isLoading={isUpdating}
				/>

				{/* Status Change Confirmation Modal */}
				<ConfirmationModal
					isOpen={isStatusChangeModalOpen}
					onClose={() => {
						setIsStatusChangeModalOpen(false);
						setStatusChangeData(null);
					}}
					onConfirm={handleConfirmStatusChange}
					title={`${
						statusChangeData?.newStatus ? "Activate" : "Deactivate"
					} User`}
					message={`Are you sure you want to ${
						statusChangeData?.newStatus ? "activate" : "deactivate"
					} "${statusChangeData?.userName}"?`}
					confirmButtonText={
						statusChangeData?.newStatus ? "Activate" : "Deactivate"
					}
					isLoading={isUpdating}
				/>
			</div>
		</div>
	);
};

export default UserManagementPage;
